import { findMessage } from '@bimser/components';
import { base64Converter } from '@bimser/core';
import { ServiceList } from '../../services/serviceList';
import * as DSEntities from './entities';
import {
	IFinalizeSigningPayload,
	ISignPayload,
	SignatureDataType,
	SignatureFormat
} from './entities';

//@ts-ignore
export const DSLoginData =
	'Bu imza 5070 sayılı elektronik imza kanununa göre güvenli elektronik imzadır.';
class DigitalSignatureAPI {
	ErrorCodes = {
		SIGNER_NOT_DEFINED: 10001,
		CARD_READER_NOT_SELECTED: 10002,
		CARD_READER_NOT_FOUND: 10003,
		CERTIFICATE_NOT_SELECTED: 10004,
		CERTIFICATE_NOT_FOUND: 10005,
		PASSWORD_NULL_OR_EMPTY: 10006,
		SIGN_FAILED: 10007,
		INVALID_PARAMETER: 10008,
		SIGNER_INITIALIZED_ERROR: 10009,
		SERVER_ERROR: 10010,
		SERVER_ERROR_DETAIL: 10011,
		AUTHENTICATION_FAILED: 10012,
		SIGNATURE_EXPIRED: 10013,
		SIGNER_NOT_INITIALIZED: 10014,
		SIGNER_INITIALIZE_TIMEOUT: 10015,
		PWSIGNER_DOMAIN_ERROR: 10016,
		INVALID_PIN: 10017,
		EXPIRED_PIN: 10018,
		BLOCKED_PIN: 10019,
		LICENCE_KEY_NOT_PROVIDED: 10020,
		UNKNOWN_ERROR: 10021
	};

	ErrorMessages: any = {
		10001: findMessage.get('102145'),
		10002: findMessage.get('102146'),
		10003: findMessage.get('102147'),
		10004: findMessage.get('102148'),
		10005: findMessage.get('102149'),
		10006: findMessage.get('102150'),
		10007: findMessage.get('102151'),
		10008: findMessage.get('102152'),
		10009: findMessage.get('102153'),
		10010: findMessage.get('102154'),
		10011: findMessage.get('102155'),
		10012: findMessage.get('102156'),
		10013: findMessage.get('102157'),
		10014: findMessage.get('102158'),
		10015: findMessage.get('102159'),
		10016: findMessage.get('102160'),
		10017: findMessage.get('102161'),
		10018: findMessage.get('102162'),
		10019: findMessage.get('102163'),
		10020: findMessage.get('102164'),
		10021: findMessage.get('101454')
	};

	private getInnerException(exception: any): any {
		if (exception?.InnerException) {
			return this.getInnerException(exception.InnerException);
		}
		return exception?.Message || this.ErrorMessages[this.ErrorCodes.AUTHENTICATION_FAILED];
	}

	private getSignatureRequestFromFormat(
		signatureFormat: SignatureFormat,
		dataType: SignatureDataType,
		request: any
	) {
		switch (signatureFormat) {
			case SignatureFormat.CAdES: {
				if (dataType == SignatureDataType.Data) {
					return ServiceList.dm.Signature.CAdES.InitializeSigningWithData.callPromise(
						request,
						true
					);
				} else {
					return ServiceList.dm.Signature.CAdES.InitializeSigningWithDMObject.callPromise(
						request,
						true
					);
				}
			}
			case SignatureFormat.PAdES: {
				if (dataType == SignatureDataType.Data) {
					return ServiceList.dm.Signature.PAdES.InitializeSigningWithData.callPromise(
						request,
						true
					);
				} else {
					return ServiceList.dm.Signature.PAdES.InitializeSigningWithDMObject.callPromise(
						request,
						true
					);
				}
			}
			case SignatureFormat.XAdES: {
				if (dataType == SignatureDataType.Data) {
					return ServiceList.dm.Signature.XAdES.InitializeSigningWithData.callPromise(
						request,
						true
					);
				} else {
					return ServiceList.dm.Signature.XAdES.InitializeSigningWithDMObject.callPromise(
						request,
						true
					);
				}
			}
		}
	}

	private getFinalizeRequestFromFormat(request: any) {
		return ServiceList.dm.Signature.FinalizeSigning.callPromise(request, true);
	}

	sign(payload: ISignPayload) {
		return new Promise<any>((resolveSign, rejectSign) => {
			if (!payload) {
				rejectSign(this.ErrorMessages[this.ErrorCodes.UNKNOWN_ERROR]);
			}

			const requestData = {
				certificateHex: payload.certificate.certificateHex,
				signatureType: payload.signatureType,
				embeddedSignature: payload.embeddedSignature,
				[payload.dataType === SignatureDataType.DmObject ? 'secretKey' : 'data']:
					payload.dataType === SignatureDataType.DmObject
						? payload.secretKey
						: base64Converter.StringToBase64(DSLoginData),
				isParallel: payload.isParallel
			};

			if (payload.dataType === SignatureDataType.DmObject) {
				requestData.versionSecretKey = payload.versionSecretKey;
			}

			this.getSignatureRequestFromFormat(
				payload.signatureFormat,
				payload.dataType,
				requestData
			)
				.then((initializeResponse: any) =>
					resolveSign({
						...initializeResponse,
						digestToBeSigned: initializeResponse.digestBase64
					})
				)
				.catch(() => {
					rejectSign(this.ErrorMessages[this.ErrorCodes.SIGN_FAILED]);
				});
		});
	}

	final(payload: IFinalizeSigningPayload) {
		return new Promise<any>((resolveFinal, rejectFinal) => {
			if (!payload) {
				rejectFinal(this.ErrorMessages[this.ErrorCodes.UNKNOWN_ERROR]);
			}

			const requestData = {
				signature: payload.signature,
				transactionUUID: payload.transactionUUID,
				secretKey: payload.secretKey
			};

			this.getFinalizeRequestFromFormat(requestData)
				.then(finalizeResponse => {
					if (payload.type === 'dm') {
						resolveFinal(null);
					} else if (finalizeResponse.signed) {
						return this._validateAuthentication(
							finalizeResponse.signatureData,
							resolveFinal,
							rejectFinal
						);
					}
				})
				.catch(err => {
					rejectFinal(this.getInnerException(err?.exception?.data?.exception));
				});
		});
	}

	private _validateAuthentication(signedData: any, resolve: Function, reject: Function) {
		ServiceList.login.Login.LoginWithDigitalSignature.callPromise(
			{
				language: 'tr-TR',
				signatureData: signedData
			},
			true
		)
			.then(res => {
				if (res) {
					resolve({
						token: res.token,
						tempTokenKey: res.tempTokenKey,
						mfaEnabled: res.mfaEnabled
					});
				}
			})
			.catch(() => {
				reject(this.ErrorMessages[this.ErrorCodes.AUTHENTICATION_FAILED]);
			});
	}
}

export { DSEntities, DigitalSignatureAPI };
